import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import GoogleCalendarWidget from '../components/intake/GoogleCalendarWidget';
import IntakeSurveySection from '../components/intake/IntakeSurveySection';
import { HONEYPOT_FIELD, useIntakeSurvey } from '../hooks/useIntakeSurvey';
import HeroSection from '../theme/HeroSection';
import { IntakeLane } from '../types/intakeSurvey';

const LANE_TITLES: Record<IntakeLane, string> = {
  athlete: 'Start your growth journey',
  coach: "Let's schedule a demo",
  parent: 'Ready to Guide Their Next Step',
  organization: 'Book a Demo call to get started',
};

const VALID_LANES: IntakeLane[] = ['athlete', 'coach', 'parent', 'organization'];

const CALENDAR_LANES: IntakeLane[] = ['coach', 'organization'];

const CALENDAR_LABELS: Partial<Record<IntakeLane, string>> = {
  coach: 'Schedule a demo',
  organization: 'Book a demo call',
};

function isIntakeLane(value: string | undefined): value is IntakeLane {
  return !!value && VALID_LANES.includes(value as IntakeLane);
}

const IntakeSurveyPage: React.FC = () => {
  const { lane: laneParam } = useParams<{ lane: string }>();
  const lane = isIntakeLane(laneParam) ? laneParam : 'athlete';

  const {
    visibleSections,
    answers,
    honeypot,
    setHoneypot,
    loading,
    submitting,
    error,
    fieldErrors,
    completed,
    completionMessage,
    setAnswer,
    submit,
  } = useIntakeSurvey(lane);

  if (!isIntakeLane(laneParam)) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error">Invalid intake survey.</Alert>
      </Container>
    );
  }

  const title = LANE_TITLES[lane];

  return (
    <Box>
      <HeroSection minHeight="35vh" showWave imageUrl="/images/SeanValentine-HGP.jpg">
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="sm" sx={{ py: 6 }}>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && completed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {completionMessage}
          </Alert>
        )}

        {!loading && !completed && visibleSections.length > 0 && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              const submitter = (e.nativeEvent as SubmitEvent).submitter;
              if (submitter instanceof HTMLElement && submitter.closest('[data-calendar-widget]')) {
                return;
              }
              void submit();
            }}
            noValidate
          >
            <Box
              component="input"
              type="text"
              name={HONEYPOT_FIELD}
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              sx={{
                position: 'absolute',
                left: '-9999px',
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: 'none',
              }}
            />

            {visibleSections.map((group) => (
              <IntakeSurveySection
                key={group.section.id}
                group={group}
                answers={answers}
                fieldErrors={fieldErrors}
                onChange={setAnswer}
              />
            ))}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, mt: 4, width: '100%' }}>
              {CALENDAR_LANES.includes(lane) && (
                <GoogleCalendarWidget label={CALENDAR_LABELS[lane]} />
              )}

              <Button type="submit" variant="contained" disabled={submitting} sx={{ minWidth: 140 }}>
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default IntakeSurveyPage;
